import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { 
  Building2, 
  Users, 
  MapPin, 
  Mail, 
  Phone,
  Calendar,
  BarChart3,
  Edit,
  Archive,
  Eye,
  MoreVertical,
  Trash2,
  RotateCcw,
  Target
} from "lucide-react";
import SubsidiaryHierarchyBreadcrumb from "./SubsidiaryHierarchyBreadcrumb";
import { useCurrency } from "../../contexts/CurrencyContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DetailedSubsidiaryCard = ({ 
  subsidiary, 
  subsidiaries = [],
  onManageTeam, 
  onViewPlans, 
  onViewPerformance,
  onEdit,
  onArchive 
}) => {
  const { formatCurrency } = useCurrency();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  // Find child subsidiaries
  const childSubsidiaries = subsidiaries.filter(s => s.parent_id === subsidiary.id);

  useEffect(() => {
    fetchMetrics();
  }, [subsidiary.id]);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      // Fetch business plans
      const plansResponse = await axios.get(`${BACKEND_URL}/api/business-plans`);
      const allPlans = plansResponse.data || [];
      
      // Match plans by company name
      const subsidiaryPlans = allPlans.filter(plan => 
        plan.company_name?.toLowerCase().includes(subsidiary.name.toLowerCase()) ||
        plan.name?.toLowerCase().includes(subsidiary.name.toLowerCase())
      );

      let totalRevenue = 0;
      let performanceScore = 0;

      // Fetch revenue for plans
      for (const plan of subsidiaryPlans) {
        try {
          const revenueResponse = await axios.get(`${BACKEND_URL}/api/revenue/${plan.id}`);
          const revenues = revenueResponse.data || [];
          
          const planRevenue = revenues.reduce((sum, rev) => {
            return sum + (parseFloat(rev.total_revenue) || 0);
          }, 0);
          totalRevenue += planRevenue;
        } catch (err) {
          console.error(`Error fetching revenue for plan ${plan.id}:`, err);
        }
      }

      // Simple performance score based on revenue
      if (totalRevenue > 0) {
        performanceScore = Math.min(Math.round((totalRevenue / 100000) * 100), 100);
      }

      setMetrics({
        plansCount: subsidiaryPlans.length,
        totalRevenue: totalRevenue,
        performanceScore: performanceScore
      });
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics({
        plansCount: 0,
        totalRevenue: 0,
        performanceScore: 0
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <SubsidiaryHierarchyBreadcrumb 
                subsidiary={subsidiary} 
                subsidiaries={subsidiaries}
              />
              {subsidiary.code && (
                <div className="text-xs text-gray-500 mt-1">Code: {subsidiary.code}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => onEdit(subsidiary)}>
              <Edit className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(subsidiary)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Subsidiary
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onManageTeam(subsidiary)}>
                  <Users className="h-4 w-4 mr-2" />
                  Manage Team
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onViewPlans(subsidiary)}>
                  <Target className="h-4 w-4 mr-2" />
                  View Plans
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onViewPerformance(subsidiary)}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Performance
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {subsidiary.is_active !== false ? (
                  <DropdownMenuItem 
                    onClick={() => onArchive(subsidiary)}
                    className="text-red-600 focus:text-red-700"
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    Archive
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem 
                    onClick={() => onArchive(subsidiary)}
                    className="text-green-600 focus:text-green-700"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Restore
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Basic Info Section */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">BASIC INFO</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Status:</span>
              <Badge 
                variant={subsidiary.is_active !== false ? "default" : "secondary"}
                className={subsidiary.is_active !== false ? "bg-green-100 text-green-700" : ""}
              >
                {subsidiary.is_active !== false ? "✅ Active" : "Archived"}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className="text-gray-600">Created:</span>
              <span className="font-medium">
                {new Date(subsidiary.created_at).toLocaleDateString()}
              </span>
            </div>
            {subsidiary.location && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Location:</span>
                <span className="font-medium">{subsidiary.location}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-400" />
              <span className="text-gray-600">Level:</span>
              <span className="font-medium">
                {subsidiary.parent_id ? "Child Subsidiary" : "Top Level"}
              </span>
            </div>
          </div>
        </div>

        {/* Details Section */}
        {(subsidiary.description || subsidiary.address || subsidiary.phone || subsidiary.email) && (
          <div className="border rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">DETAILS</h4>
            <div className="space-y-2 text-sm">
              {subsidiary.description && (
                <div>
                  <span className="text-gray-600">Description:</span>
                  <p className="text-gray-900 mt-1">{subsidiary.description}</p>
                </div>
              )}
              {subsidiary.address && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <span className="text-gray-600">Address:</span>
                    <p className="text-gray-900">{subsidiary.address}</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {subsidiary.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-medium">{subsidiary.phone}</span>
                  </div>
                )}
                {subsidiary.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{subsidiary.email}</span>
                  </div>
                )}
              </div>
              {subsidiary.manager && (
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Manager:</span>
                  <span className="font-medium">{subsidiary.manager}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Metrics Section */}
        <div className="border rounded-lg p-4 bg-blue-50">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">METRICS</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
                <Users className="h-4 w-4" />
                <span className="text-xs">Team</span>
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {subsidiary.team_count || 0}
              </div>
              <div className="text-xs text-gray-500">members</div>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
                <BarChart3 className="h-4 w-4" />
                <span className="text-xs">Plans</span>
              </div>
              <div className="text-2xl font-bold text-green-600">
                {loading ? "..." : (metrics?.plansCount || 0)}
              </div>
              <div className="text-xs text-gray-500">active</div>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
                <BarChart3 className="h-4 w-4" />
                <span className="text-xs">Revenue</span>
              </div>
              <div className="text-2xl font-bold text-purple-600">
                {loading ? "..." : metrics?.totalRevenue > 0 ? formatCurrency(metrics.totalRevenue) : formatCurrency(0)}
              </div>
              <div className="text-xs text-gray-500">YTD</div>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
                <BarChart3 className="h-4 w-4" />
                <span className="text-xs">Performance</span>
              </div>
              <div className="text-2xl font-bold text-green-600">
                {loading ? "..." : `${metrics?.performanceScore || 0}%`}
              </div>
              <div className="text-xs text-gray-500">score</div>
            </div>
          </div>
        </div>

        {/* Child Subsidiaries Section */}
        {childSubsidiaries.length > 0 && (
          <div className="border rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              CHILD SUBSIDIARIES ({childSubsidiaries.length})
            </h4>
            <div className="space-y-2">
              {childSubsidiaries.map((child) => (
                <div key={child.id} className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">{child.name}</span>
                  <span className="text-gray-500">({child.team_count || 0} team members)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onManageTeam(subsidiary)}
          >
            <Users className="h-4 w-4 mr-2" />
            Manage Team
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onViewPlans && onViewPlans(subsidiary)}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            View Plans
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onViewPerformance && onViewPerformance(subsidiary)}
          >
            <Eye className="h-4 w-4 mr-2" />
            View Performance
          </Button>
          {subsidiary.is_active !== false && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onArchive(subsidiary)}
            >
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DetailedSubsidiaryCard;
